import React, { lazy, Suspense } from "react";
import {
	BrowserRouter as Router,
	Route,
	Navigate,
	Routes,
	useParams,
} from "react-router-dom";

// Importing CSS files for lazy loading support (no broken styles) -Omar
import "./App.css";
import { AnimatePresence } from "framer-motion";

// Changed pages to lazy loaded pages instead of importing all pages at once -Omar
import ScrollToButton from "./components/ScrollToButton";
import NotFound from "./components/NotFound";
import ThemeScope from "./components/ThemeScope";
import RouteAnalytics from "./components/RouteAnalytics";
import ChunkErrorBoundary, {
	RouteLoading,
} from "./components/ChunkErrorBoundary";
// The six configurator steps share ONE chunk, so moving between steps never
// waits on another download (a cache miss at the origin took 30 s on live).
const Customize_Details = lazy(() =>
	import(/* webpackChunkName: "customize" */ "./screens/customize/details")
);
const Customize_Fabric = lazy(() =>
	import(/* webpackChunkName: "customize" */ "./screens/customize/fabric")
);
const Customize_BottomBar = lazy(() =>
	import(/* webpackChunkName: "customize" */ "./screens/customize/bottombar")
);
const Customize_Control = lazy(() =>
	import(/* webpackChunkName: "customize" */ "./screens/customize/control")
);
const Customize_Valance = lazy(() =>
	import(/* webpackChunkName: "customize" */ "./screens/customize/valance")
);
const Customize_Summary = lazy(() =>
	import(/* webpackChunkName: "customize" */ "./screens/customize/summary")
);
const FooterSlot = lazy(() => import("./components/FooterSlot"));
const Header = lazy(() => import("./components/header"));
const CartScreen = lazy(() => import("./screens/CartScreen"));
const VisualizerScreen = lazy(() =>
	import(/* webpackChunkName: "visualizer" */ "./screens/visualizer")
);

const InstallationScreen = lazy(() =>
	import("./screens/guides/InstallationScreen")
);
const MeasuringGuideScreen = lazy(() =>
	import("./screens/guides/MeasuringGuideScreen")
);
import NewHome from "./screens/NewHome";
const LandingPage = lazy(() => import("./screens/LandingPage"));
const ProductSelectionGuideScreen = lazy(() =>
	import("./screens/guides/ProductSelectionGuideScreen")
);
const ShippingPolicyScreen = lazy(() =>
	import("./screens/guides/ShippingPolicyScreen")
);
const ReturnPolicyScreen = lazy(() =>
	import("./screens/guides/ReturnPolicyScreen")
);
const PrivacyPolicy = lazy(() => import("./screens/guides/PrivacyPolicy"));
const TermsAndConditions = lazy(() =>
	import("./screens/guides/TermsAndConditions")
);
const WarrantyScreen = lazy(() => import("./screens/guides/WarrantyScreen"));
const FabricCertifications = lazy(() =>
	import("./screens/guides/FabricCertifications")
);
const LoginScreen = lazy(() => import("./screens/LoginScreen"));
const RecoveryScreen = lazy(() => import("./screens/RecoveryScreen"));
const PasswordResetScreen = lazy(() => import("./screens/PasswordResetScreen"));
const RegisterScreen = lazy(() => import("./screens/RegisterScreen"));
const ProfileScreen = lazy(() => import("./screens/ProfileScreen"));
const PlaceOrderScreen = lazy(() => import("./screens/PlaceOrderScreen"));
const ConfirmationScreen = lazy(() => import("./screens/ConfirmationScreen"));
const MyOrderScreen = lazy(() => import("./screens/MyOrderScreen"));
const ReferScreen = lazy(() => import("./screens/ReferFriend"));

const Contact = lazy(() => import("./screens/contact"));
const Guides = lazy(() => import("./screens/guides"));
const Showroom = lazy(() => import("./screens/showroom"));
const CustomizeShade = lazy(() => import("./screens/shop"));
const CustomizeShadeInfo = lazy(() => import("./screens/shop/info"));
const RoomShades = lazy(() => import("./screens/shop/rooms"));
const TorontoPage = lazy(() => import("./screens/toronto"));
const BlackoutBlindsPage = lazy(() => import("./screens/blackout"));
const FabricSwatch = lazy(() => import("./screens/FabricSwatch"));
const Magazine = lazy(() => import("./screens/Magazine"));
const SwatchCheckout = lazy(() => import("./screens/SwatchCheckout"));
const Blog = lazy(() => import("./screens/blog"));
const BlogContent = lazy(() => import("./screens//blog/content"));
const AboutUs = lazy(() => import("./screens/about"));
const MotorizedSmart = lazy(() => import("./screens/shop/motorized-solutions"));
const FAQ = lazy(() => import("./screens/FAQScreen"));
const ProductQuiz = lazy(() => import("./components/ProductQuiz"));
const AutoScrollTop = lazy(() => import("./components/AutoScrollTop"));
const ReviewRequestScreen = lazy(() => import("./screens/ReviewRequestScreen"));

function ExploreToShopRedirect() {
	const { id } = useParams();
	return <Navigate replace to={`/shop/info/${id}`} />;
}

const App = () => {
	document.body.style = "background: #fafaf7;"; // site background (new design)

	return (
		// Suspense element used for lazy loading; the boundary recovers from a
		// chunk that no longer exists after a deploy (otherwise: blank page)
		<ChunkErrorBoundary>
		<Suspense fallback={<RouteLoading />}>
			<Router>
				<ThemeScope />
				<RouteAnalytics />
				<AnimatePresence mode="popLayout">
					<Header />
					<AutoScrollTop />
					<Routes>
						<Route path="/special-offer" exact element={<LandingPage />} />
						<Route
							path="/customize/details"
							exact
							element={<Customize_Details />}
						/>
						<Route
							path="/customize/fabric"
							exact
							element={<Customize_Fabric />}
						/>
						<Route
							path="/customize/bottombar"
							exact
							element={<Customize_BottomBar />}
						/>
						<Route
							path="/customize/control"
							exact
							element={<Customize_Control />}
						/>
						<Route
							path="/customize/valance"
							exact
							element={<Customize_Valance />}
						/>
						<Route
							path="/customize/summary"
							exact
							element={<Customize_Summary />}
						/>
						<Route path="/about" element={<AboutUs />} />
						<Route path="/contact" element={<Contact />} />
						<Route path="/cart" element={<CartScreen />} />
						<Route path="/visualizer" element={<VisualizerScreen />} />
						{/* <Route path="/guides" exact element={<Guides />} /> */}
						<Route
							path="/guides/productselection"
							exact
							element={<ProductSelectionGuideScreen />}
						/>
						<Route
							path="/guides/measuring-guide"
							exact
							element={<MeasuringGuideScreen />}
						/>
						<Route
							path="/guides/installation-guide"
							exact
							element={<InstallationScreen />}
						/>

						{/* Redirects for legacy /guides/* URLs → current pages */}
						<Route
							path="/guides/privacy-policy"
							element={<Navigate replace to="/help/privacy-policy" />}
						/>
						<Route
							path="/guides/shipping-policy"
							element={<Navigate replace to="/help/shipping-policy" />}
						/>
						<Route
							path="/guides/return-policy"
							element={<Navigate replace to="/help/return-policy" />}
						/>
						<Route
							path="/guides/terms-conditions"
							element={<Navigate replace to="/help/terms-conditions" />}
						/>
						<Route
							path="/guides/warranty"
							element={<Navigate replace to="/help/warranty" />}
						/>
						<Route
							path="/guides/blog"
							element={<Navigate replace to="/blog" />}
						/>
						<Route path="/guides" element={<Navigate replace to="/blog" />} />

						<Route
							path="/help/privacy-policy"
							exact
							element={<PrivacyPolicy />}
						/>
						<Route
							path="/help/shipping-policy"
							exact
							element={<ShippingPolicyScreen />}
						/>
						<Route
							path="/help/return-policy"
							exact
							element={<ReturnPolicyScreen />}
						/>
						<Route
							path="/help/terms-conditions"
							exact
							element={<TermsAndConditions />}
						/>
						<Route path="/help/faq" exact element={<FAQ />} />
						<Route path="/help/warranty" exact element={<WarrantyScreen />} />
						<Route
							path="/help/fabric-certifications"
							exact
							element={<FabricCertifications />}
						/>
						<Route
							path="/help/guarantee"
							element={<Navigate replace to="/help/warranty" />}
						/>
						<Route path="/login" exact element={<LoginScreen />} />
						<Route
							path="/account-recovery"
							exact
							element={<RecoveryScreen />}
						/>
						<Route
							path="/reset-password"
							exact
							element={<PasswordResetScreen />}
						/>
						{/* Recovery emails sent before Oct 2026 link here */}
						<Route path="/resetPassword" element={<PasswordResetScreen />} />
						<Route path="/register" exact element={<RegisterScreen />} />
						<Route path="/profile" exact element={<ProfileScreen />} />
						<Route path="/myorders" exact element={<MyOrderScreen />} />
						<Route path="/placeorder" exact element={<PlaceOrderScreen />} />
						<Route
							path="/confirmation"
							exact
							element={<ConfirmationScreen />}
						/>
						<Route path="/refer-a-friend" exact element={<ReferScreen />} />

						<Route path="/" exact element={<NewHome />} />
						<Route path="/shop" exact element={<CustomizeShade />} />
						<Route path="/explore" element={<Navigate replace to="/shop" />} />
						<Route path="/blinds/all" element={<Navigate replace to="/shop" />} />
						{/* Vertical Sheer was renamed Dream Shades (Oct 2026); keep the old URL alive. */}
						<Route
							path="/shop/info/vertical-sheer"
							element={<Navigate replace to="/shop/info/dream-shades" />}
						/>
						<Route
							path="/shop/info/:id"
							exact
							element={<CustomizeShadeInfo />}
						/>
						<Route path="/shades/:slug" element={<RoomShades />} />
						<Route path="/blinds-toronto" element={<TorontoPage />} />
						<Route path="/blackout-blinds" element={<BlackoutBlindsPage />} />
						<Route
							path="/explore/info/:id"
							element={<ExploreToShopRedirect />}
						/>

						<Route
							path="/motorized-solutions"
							exact
							element={<MotorizedSmart />}
						/>

						<Route path="/quiz" exact element={<ProductQuiz />} />
						<Route path="/swatches" exact element={<FabricSwatch />} />
						<Route path="/magazine" exact element={<Magazine />} />
						<Route path="/showroom" exact element={<Showroom />} />
						<Route path="/swatchcheckout" exact element={<SwatchCheckout />} />

						<Route path="/blog" exact element={<Blog />} />

						<Route path="/blog/:id" exact element={<BlogContent />} />
						{/* Landing page for review-request emails */}
						<Route path="/review/:token" element={<ReviewRequestScreen />} />
						<Route path="/404" element={<NotFound />} />
						<Route path="*" element={<Navigate replace to="/404" />} />
					</Routes>
				</AnimatePresence>
				<FooterSlot />
				<ScrollToButton />
			</Router>
		</Suspense>
		</ChunkErrorBoundary>
	);
};

export default App;
